import {
    Box, Skeleton, SxProps, Theme,
} from '@mui/material';
import { useEffect, useState } from 'react';

type LoadingStateImageProps = {
    src?: string;
    imageSx?: SxProps<Theme>;
    skeletonWidth?: number;
    skeletonHeight?: number;
    fullWidth?: boolean;
    maxRetries?: number;
};

export const LoadingStateImage = ({
    src, imageSx, skeletonWidth = 500, skeletonHeight = 400, fullWidth = false, maxRetries = 0,
}: LoadingStateImageProps): JSX.Element => {
    const [imageLoading, setImageLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setImageLoading(true);
        setAttempt(0);
        setFailed(false);
    }, [src]);

    const handleError = (): void => {
        if (attempt < maxRetries) {
            setImageLoading(true);
            // Remounting the img via the key change re-requests the same url without
            // mutating it, so signed preview urls keep their signature intact.
            setAttempt((prev) => prev + 1);
        } else {
            setImageLoading(false);
            setFailed(true);
        }
    };

    if (!src || failed) {
        return (
            <Box
                sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                    width: 400,
                    height: 300,
                }}
            >
                {failed ? 'Failed to load image' : 'No image to show'}
            </Box>
        );
    }

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
                position: 'relative',
                display: fullWidth ? 'block' : 'inline-block',
                width: fullWidth ? '100%' : 'auto',
                maxWidth: '100%',
            }}
            >
                {imageLoading && (
                    <Skeleton
                        animation="wave"
                        height={skeletonHeight}
                        sx={{ borderRadius: 1 }}
                        variant="rectangular"
                        width={skeletonWidth}
                    />
                )}
                <Box
                    component="img"
                    key={attempt}
                    src={src}
                    sx={imageLoading
                        ? {
                            position: 'absolute', top: 0, left: 0, opacity: 0, pointerEvents: 'none',
                        }
                        : {
                            maxWidth: '100%', maxHeight: '100%', height: 'auto', ...imageSx,
                        }}
                    onError={handleError}
                    onLoad={(): void => setImageLoading(false)}
                />
            </Box>
        </Box>
    );
};
